import { Spinner } from '@frontend/components/common';
import { DeviceActuatorClient } from '@frontend/device/api';
import { useReactiveDevice } from '@frontend/device/events';
import { Device } from '@frontend/device/types';
import { ManageGates as ManageGatesIcon } from '@frontend/icons';
import { useReactiveIoT } from '@frontend/iot/events';
import { CommonMessage } from '@frontend/lang';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import useManageGates from './manage-gates.controller';

const ID = 'manage-gates';

const ManageGates = () => {
    const viewProps = useManageGates();

    return (
        <WizardScreenContainer id={ID}>
            <div className='text-foreground-default justify-start'>
                <FormattedMessage
                    id='kiosk.wizard.manage-gates.title'
                    defaultMessage='Manage gates'
                />
            </div>
            {viewProps.isLoading ? (
                <div className='flex size-full flex-col items-center justify-center'>
                    <Spinner />
                </div>
            ) : (
                <div className='inline-flex flex-col items-start justify-start gap-2 self-stretch'>
                    <div className='grid grid-cols-2 gap-2 self-stretch'>
                        {viewProps.gates.map((gate) => (
                            <GateCard
                                key={gate.id}
                                gate={gate}
                            />
                        ))}
                    </div>
                </div>
            )}
        </WizardScreenContainer>
    );
};

export default ManageGates;

const GateCard = ({ gate }: { gate: Device }) => {
    const { iot, isLoading: isIoTLoading } = useReactiveIoT({ iotId: gate.iot_id });
    const { device, isLoading: isDeviceLoading } = useReactiveDevice({ deviceId: gate.id });
    const isLoading = isIoTLoading || isDeviceLoading;

    const [isOpen, setIsOpen] = useState(gate.status === 'open' || false);
    const [isToggling, setIsToggling] = useState(false);

    const toggleGate = async () => {
        if (isToggling) return;
        setIsToggling(true);
        await DeviceActuatorClient.openDevice(gate.account_id, gate.iot_id, gate.id);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsToggling(false);
    };

    useEffect(() => {
        if (device) {
            setIsOpen(device.status === 'open');
        }
    }, [device]);

    const isOffline = iot?.status !== 'online' && !isLoading;

    return (
        <div className='bg-background-surface border-border-default inline-flex flex-1 flex-col items-start justify-start gap-3 rounded-xl border p-3'>
            <div className='inline-flex items-start justify-between self-stretch'>
                <ManageGatesIcon className='text-brand-muted size-6' />
                {isOffline ? (
                    <div className='bg-status-danger inline-flex items-start justify-center gap-1 rounded-full px-2 py-1'>
                        <div className='text-background-surface justify-start text-center text-sm font-semibold'>
                            <FormattedMessage
                                id='gate.offline'
                                defaultMessage='Offline'
                            />
                        </div>
                    </div>
                ) : isToggling || isLoading ? (
                    <Spinner className='text-border-default fill-brand-primary size-7' />
                ) : (
                    <div
                        data-variant={isOpen ? 'Success' : 'Muted'}
                        className={`px-2 py-1 ${isOpen ? 'bg-status-success' : 'bg-brand-muted'} inline-flex items-start justify-center gap-1 rounded-full`}>
                        <div className='text-background-surface justify-start text-center text-sm font-semibold'>
                            {isOpen ? CommonMessage.OBJECTS.SLOT.STATUS.OPEN : CommonMessage.OBJECTS.SLOT.STATUS.CLOSED}
                        </div>
                    </div>
                )}
            </div>
            <div className='text-foreground-default justify-start text-base font-semibold'>{device?.name || gate.name}</div>
            <button
                disabled={isToggling || isLoading || isOffline}
                className='text-brand-primary disabled:text-brand-disabled justify-start text-base font-medium'
                onClick={toggleGate}>
                {isOpen ? (
                    <FormattedMessage
                        id='gate.close'
                        defaultMessage='Close gate'
                    />
                ) : (
                    <FormattedMessage
                        id='gate.open'
                        defaultMessage='Open gate'
                    />
                )}
            </button>
        </div>
    );
};
